import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { platformLabel, type Platform } from "@/platform/Platform";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { hexToCss } from "@/ui/colors";
import { endlessLeaderboardService } from "@/endless/EndlessLeaderboardService";
import { attemptEndlessSubmission } from "@/endless/onboardingSubmission";
import { ENDLESS_RULES_VERSION, type EndlessLeaderboardEntry, type EndlessMyRank, type EndlessSubmissionMetrics } from "@/endless/types";
import { formatSurvivalTime } from "@/endless/formatSurvivalTime";
import { OnlineFeaturesStorage } from "@/online/OnlineFeaturesStorage";
import { AuthService } from "@/online/AuthService";
import { getLeaderboardService } from "@/online/LeaderboardService";
import { flushOnlineQueues } from "@/online/bootstrap";
import { createOnlineFeaturesModal, type OnlineFeaturesModalHandle } from "@/ui/OnlineFeaturesModal";
import { createDataPrivacyModal, type DataPrivacyModalHandle } from "@/ui/DataPrivacyModal";
import { createNameEntryModal, type NameEntryModalHandle } from "@/ui/NameEntryModal";
import { desiredTitleSize, computeHeaderLayout, rowContentBottom } from "@/ui/leaderboardLayout";
import { buttonCenterYBelow } from "@/ui/buttonLayout";
import {
  trackEndlessLeaderboardOpened,
  trackEndlessSubmissionAttempted,
  trackEndlessSubmissionAccepted,
  trackEndlessSubmissionRejected,
  trackEndlessSubmissionFailed,
  trackEndlessNewOnlineBest,
} from "@/analytics/gameAnalytics";
import type { EndlessLeaderboardOpenSource } from "@/analytics/events";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

export interface EndlessLeaderboardSceneData {
  returnScene?: string;
  /** Where this scene was opened from - "menu" (default) or "result_screen". Analytics only. */
  source?: EndlessLeaderboardOpenSource;
  /**
   * Post-death UX: `EndlessResultScene`'s still-unsubmitted run, carried
   * over ONLY when the player pressed VIEW LEADERBOARD before the run
   * could be submitted there (Online Features not enabled yet) -
   * `undefined` for every other entry point (Main Menu, an opted-in
   * player whose run already submitted itself). PR34: the run is also
   * already kept on the device by `EndlessLeaderboardService.captureResult()`,
   * so this in-memory copy is only what lets an ENABLE here report THIS
   * run's own result. See `trySubmitPendingRun()`.
   */
  pendingMetrics?: EndlessSubmissionMetrics;
}

type LoadState = "loading" | "ok" | "empty" | "offline" | "error";

const PAGE_SIZE = 25;
const ROW_HEIGHT = 44;
const LIST_WIDTH_MAX = 460;

/**
 * The global Endless leaderboard - a separate scene, deliberately never
 * retrofitted into Campaign's per-world/per-level `LeaderboardScene`
 * (Endless has no world/level axis at all) and never mixed with
 * `DailyLeaderboardScene` (a different ranking model entirely). Reuses
 * `DailyLeaderboardScene`'s proven paginated-list pattern: the ENTIRE
 * `rank <= 100` result set is fetched in ONE call (never assume exactly
 * 100 rows - a tie at rank 100 can return a few more), then revealed
 * client-side in pages of ~25 via LOAD MORE - no additional network calls
 * for pagination.
 *
 * Viewing never requires Online Features or a submitted identity (see
 * `EndlessLeaderboardService.isLeaderboardAvailable()`) - anyone can open
 * this scene and see the Top 100. Only the caller's OWN rank additionally
 * requires the Online Features opt-in; a player who hasn't opted in sees a
 * compact JOIN prompt instead of a "YOU" row, reusing the exact same
 * `OnlineFeaturesModal` Campaign's `LeaderboardScene` already uses.
 */
export class EndlessLeaderboardScene extends Phaser.Scene {
  private returnScene = "MenuScene";
  private requestToken = 0;

  private loadState: LoadState = "loading";
  private errorMessage = "";
  private rows: EndlessLeaderboardEntry[] = [];
  private visibleCount = PAGE_SIZE;
  private myRank: EndlessMyRank | null = null;
  private myDisplayName: string | null = null;
  /** PR34: see `create()`. */
  private platform: Platform = "desktop";
  /** See `EndlessLeaderboardSceneData.pendingMetrics`'s own doc. `null` for every entry point except a fresh, still-unsubmitted Endless result. */
  private pendingMetrics: EndlessSubmissionMetrics | null = null;
  /** Mirrors `EndlessResultScene`'s own flag - once true, `trySubmitPendingRun()` is a permanent no-op for this run, so a stray retry (ENABLE, then SET NAME) can never submit it twice. */
  private submissionSettled = false;
  /** Guards `trySubmitPendingRun()`'s async continuation against updating a scene that has already shut down - mirrors `EndlessResultScene.isSceneActive`. */
  private isSceneActive = false;

  private mainContainer!: Phaser.GameObjects.Container;
  private listContainer!: Phaser.GameObjects.Container;
  private onlineModal!: OnlineFeaturesModalHandle;
  private privacyModal!: DataPrivacyModalHandle;
  private nameModal!: NameEntryModalHandle;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("EndlessLeaderboardScene");
  }

  create(data: EndlessLeaderboardSceneData): void {
    this.returnScene = data.returnScene ?? "MenuScene";
    // PR34: the viewer's own Mobile/Desktop board, decided once per visit -
    // the list and the "YOU" rank both read it. No toggle.
    this.platform = detectPlatform();
    trackEndlessLeaderboardOpened(data.source ?? "menu");
    this.requestToken = 0;
    this.loadState = "loading";
    this.rows = [];
    this.visibleCount = PAGE_SIZE;
    this.myRank = null;
    this.myDisplayName = null;
    this.pendingMetrics = data.pendingMetrics ?? null;
    this.submissionSettled = false;
    this.isSceneActive = true;

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);

    this.privacyModal = createDataPrivacyModal(this, 95, () => this.privacyModal.setVisible(false));
    this.onlineModal = createOnlineFeaturesModal(this, 90, {
      onEnable: () => this.handleEnableOnline(),
      onPlayOffline: () => this.onlineModal.setVisible(false),
      onDataPrivacy: () => this.privacyModal.setVisible(true),
    });
    this.nameModal = createNameEntryModal(this, 96, {
      onSave: (name) => this.handleSaveName(name),
      onNotNow: () => {},
    });

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    void this.refresh();
    // Fast path for an already-opted-in player (named or not - PR34) -
    // submits `pendingMetrics` immediately. A no-op whenever there's no
    // pending run at all (every entry point except a fresh,
    // still-unsubmitted Endless result - see `EndlessLeaderboardSceneData`).
    void this.trySubmitPendingRun();
  }

  private teardown(): void {
    this.isSceneActive = false;
    this.scale.off("resize", this.onResize, this);
    this.onlineModal.destroy();
    this.privacyModal.destroy();
    this.nameModal.destroy();
  }

  private onResize(): void {
    this.buildLayout();
    this.onlineModal.relayout();
    this.privacyModal.relayout();
    this.nameModal.relayout();
  }

  private goBack(): void {
    this.scene.start(this.returnScene);
  }

  private handleEnableOnline(): void {
    OnlineFeaturesStorage.setEnabled(true);
    this.onlineModal.setVisible(false);
    // PR34: flushes BOTH queues (Campaign and Endless) - anything captured
    // on this device before the opt-in is sent now, under the player's
    // current anonymous identity, once a valid session exists.
    void flushOnlineQueues();
    void this.refresh();
    // A player who enables Online Features right here, with a pending run
    // already carried over from EndlessResultScene, still gets it
    // submitted automatically, named or not (PR34). PR31 follow-up fix:
    // EndlessResultScene itself no longer has an ENABLE control of its
    // own at all (see that scene's own doc) - this is now the only place
    // that can happen.
    void this.trySubmitPendingRun();
  }

  /** Explicit action - the modal focuses its input immediately (the default), same as before this PR. */
  private openEditName(): void {
    this.nameModal.open({ headline: "EDIT NAME", initialValue: this.myDisplayName ?? "", autoFocus: true });
  }

  /**
   * Post-death UX: the explicit control for a player who has NO valid
   * saved name yet (see `renderSetNameCta()`) - never opened
   * automatically. Only this explicit press focuses the input/opens the
   * mobile keyboard.
   */
  private openSetName(): void {
    this.nameModal.open({ headline: "SET NAME", contextLine: "Set your name to join the global ranking.", autoFocus: true });
  }

  private async handleSaveName(name: string): Promise<{ ok: boolean; error?: string }> {
    const result = await getLeaderboardService().setDisplayName(name);
    if (result.state !== "ok") {
      return { ok: false, error: result.state === "error" ? result.message : "Could not save name right now." };
    }
    this.myDisplayName = result.data;
    // PR34: saving a name never submits anything - submission no longer
    // depends on a name at all, and every historical row for this player
    // already picks the new name up through the live profile join.
    // A full refresh, not just renderList(): the already-fetched Top 100
    // rows (this.rows) were pulled before the name change and still show
    // the old (or ANONYMOUS) name for this player's own entry if they're
    // in the list - only a fresh fetch replaces that with the name just saved.
    void this.refresh();
    return { ok: true };
  }

  /**
   * Post-death UX: attempts to submit `pendingMetrics` (a run carried over
   * from `EndlessResultScene`) via the same pure `attemptEndlessSubmission()`
   * decision that Scene uses - safe to call repeatedly (on `create()`,
   * after ENABLE, after a name is saved): a no-op once `submissionSettled`
   * or when there's no pending run at all. Never opens `NameEntryModal`
   * itself on a `"need-name"` decision - only `openSetName()`/`openEditName()`
   * (both explicit button presses) ever do that.
   */
  private async trySubmitPendingRun(): Promise<void> {
    if (this.submissionSettled || !this.pendingMetrics) return;
    const metrics = this.pendingMetrics;
    const action = await attemptEndlessSubmission(metrics, {
      isSubmissionAvailable: () => endlessLeaderboardService.isSubmissionAvailable(),
      ensureSignedIn: () => AuthService.ensureSignedIn(),
      submitResult: (m) => endlessLeaderboardService.submitResult(m),
    });
    if (!this.isSceneActive || this.submissionSettled) return;

    switch (action.type) {
      case "none":
        return;
      case "submitted": {
        this.submissionSettled = true;
        trackEndlessSubmissionAttempted(metrics.endReason);
        switch (action.result.outcome) {
          case "sent":
            trackEndlessSubmissionAccepted(metrics.endReason, action.result.isNewBest ?? false, action.result.rank ?? null);
            if (action.result.isNewBest) trackEndlessNewOnlineBest(action.result.rank ?? null);
            void this.refresh();
            break;
          case "rejected":
            trackEndlessSubmissionRejected();
            break;
          case "queued":
            trackEndlessSubmissionFailed();
            break;
          case "disabled":
            break;
        }
        return;
      }
    }
  }

  private async refresh(): Promise<void> {
    const token = ++this.requestToken;
    this.loadState = "loading";
    this.renderList();

    if (!endlessLeaderboardService.isLeaderboardAvailable()) {
      if (token !== this.requestToken) return;
      this.loadState = "offline";
      this.renderList();
      return;
    }

    const wantsMyRank = endlessLeaderboardService.isSubmissionAvailable();
    const [entriesResult, rankResult, nameResult] = await Promise.all([
      endlessLeaderboardService.getLeaderboard(this.platform, ENDLESS_RULES_VERSION),
      wantsMyRank ? endlessLeaderboardService.getMyRank(this.platform, ENDLESS_RULES_VERSION) : Promise.resolve(null),
      wantsMyRank ? getLeaderboardService().getMyDisplayName() : Promise.resolve(null),
    ]);
    if (token !== this.requestToken) return;

    this.myRank = rankResult && rankResult.state === "ok" ? rankResult.data : null;
    if (nameResult && nameResult.state === "ok") this.myDisplayName = nameResult.data;

    if (entriesResult.state === "ok") {
      this.rows = entriesResult.data;
      this.visibleCount = PAGE_SIZE;
      this.loadState = entriesResult.data.length === 0 ? "empty" : "ok";
    } else {
      this.loadState = entriesResult.state === "offline" ? "offline" : "error";
      this.errorMessage = entriesResult.state === "error" ? entriesResult.message : "";
    }

    this.renderList();
  }

  private loadMore(): void {
    this.visibleCount = Math.min(this.rows.length, this.visibleCount + PAGE_SIZE);
    this.renderList();
  }

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    const { width, height } = this.scale;
    const centerX = width / 2;

    // Mobile beta fix (PR29, 3rd round): BACK and the title no longer fight
    // over a horizontal safe zone on mobile at all - below
    // `LB_MOBILE_HEADER_BREAKPOINT` (`computeHeaderLayout()`), BACK gets
    // its own row and the title gets its own row below it, centered, with
    // the FULL content width available. Human QA found the previous
    // shrink/wrap-into-a-safe-zone approach (round 2; still what desktop
    // uses, where title/BACK do still share a row) produced
    // readable-but-ugly results on mobile even once it stopped overlapping
    // BACK - this gives the title enough room that it never needs to wrap
    // in practice. `wordWrap` stays only as a defensive backstop.
    const initialLayout = computeHeaderLayout(width, height);
    const titleSize = desiredTitleSize(width);
    const title = this.add
      .text(centerX, initialLayout.titleY, "ENDLESS RANKS", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
        align: "center",
        wordWrap: { width: initialLayout.titleMaxWidth, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.mainContainer.add(title);

    // Second pass: the title's REAL measured height (a no-op whenever it
    // rendered on one line - the common case at every supported width).
    const layout = computeHeaderLayout(width, height, title.height);

    const backButton = createButton(this, layout.backX, layout.backY, "BACK", () => this.goBack(), {
      width: layout.backWidth,
      height: layout.backHeight,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(backButton);

    // Flows from the title's own REAL measured height (never assumed) - a
    // no-op whenever it rendered on one line, but keeps the subtitle clear
    // of a wrapped title in the rare case wordWrap's backstop kicked in.
    const subtitle = this.add
      .text(centerX, title.y + title.height / 2 + 6, `${platformLabel(this.platform)} · TOP 100 · RANKED BY LENGTH, THEN SCORE`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        color: "#8b93a7",
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(subtitle);

    this.listContainer = this.add.container(centerX, subtitle.y + subtitle.height + 20);
    this.mainContainer.add(this.listContainer);

    this.renderList();
  }

  private renderList(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);
    const width = Math.min(LIST_WIDTH_MAX, this.scale.width - 32);

    switch (this.loadState) {
      case "loading":
        this.renderStatusMessage(width, "LOADING…");
        return;
      case "offline":
        this.renderStatusMessage(width, "LEADERBOARD UNAVAILABLE — YOUR LOCAL BEST IS SAFE", true);
        return;
      case "error":
        this.renderStatusMessage(width, this.errorMessage || "Something went wrong.", true);
        return;
      case "empty":
        this.renderStatusMessage(width, "BE THE FIRST");
        this.renderParticipationRow(width, 60);
        return;
      case "ok":
        this.renderRows(width);
        return;
    }
  }

  private renderStatusMessage(width: number, message: string, showRetry = false): void {
    const text = this.add
      .text(0, 20, message, {
        fontFamily: "Arial, sans-serif",
        fontSize: "15px",
        fontStyle: "bold",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: width - 20 },
      })
      .setOrigin(0.5, 0);
    this.listContainer.add(text);
    if (showRetry) {
      const retryHeight = 46;
      const retry = createButton(this, 0, buttonCenterYBelow(text.y + text.height, 20, retryHeight), "RETRY", () => void this.refresh(), {
        width: 200,
        height: retryHeight,
      });
      this.listContainer.add(retry);
    }
  }

  private renderRows(width: number): void {
    const header = this.add
      .text(-width / 2, 0, "RANK", { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
      .setOrigin(0, 0.5);
    this.listContainer.add(header);

    let y = 20;
    const visible = this.rows.slice(0, this.visibleCount);
    for (const row of visible) {
      y = this.renderRow(width, y, row, row.rank === this.myRank?.rank);
    }
    y += 8;

    y = this.renderParticipationRow(width, y, visible.some((r) => r.rank === this.myRank?.rank));

    if (this.visibleCount < this.rows.length) {
      const loadMore = createButton(this, 0, y + 16, "LOAD MORE", () => this.loadMore(), {
        width: 200,
        height: 44,
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      });
      this.listContainer.add(loadMore);
    }
  }

  /**
   * Mobile beta fix (round 2): a max-16-char display name (`NAME_MAX_LENGTH`
   * in `NameEntryModal.ts`) routinely measures WIDER than the name column's
   * available `wordWrap` width on a narrow phone (confirmed via real canvas
   * text metrics), so Phaser wraps it to a second line - but every element
   * below/beside it (the ARENA CLEARED badge, the next row entirely) used to
   * assume a single line via a FIXED offset/height (`y + 16`, `ROW_HEIGHT`),
   * landing directly on top of that second line whenever it actually
   * appeared. Fixed by measuring `nameText`'s own real (post-wrap) `.height`
   * and flowing everything from that instead of a fixed assumption - a
   * no-op whenever the name fits on one line (the previous, common case).
   * Returns the Y the NEXT row must start at, replacing the caller's own
   * fixed `+= ROW_HEIGHT` stepping.
   */
  private renderRow(width: number, y: number, row: EndlessLeaderboardEntry, isMe: boolean): number {
    const rankText = this.add
      .text(-width / 2, y, `#${row.rank}`, { fontFamily: "Arial, sans-serif", fontSize: "14px", fontStyle: "bold", color: "#8b93a7" })
      .setOrigin(0, 0);

    const nameX = -width / 2 + 46;
    const nameText = this.add
      .text(nameX, y, row.displayName, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#f5f7fa",
        wordWrap: { width: width - 200 },
      })
      .setOrigin(0, 0);

    // ARENA CLEARED badge - informational only, never a ranking input.
    // Positioned below the name's own real (possibly 2-line) height, never
    // a fixed offset that assumed a single line.
    let badge: Phaser.GameObjects.Text | null = null;
    if (row.endReason === "arena_cleared") {
      badge = this.add
        .text(nameX, y + nameText.height + 2, "ARENA CLEARED", {
          fontFamily: "Arial, sans-serif",
          fontSize: "9px",
          fontStyle: "bold",
          color: hexToCss(GameConfig.colors.star),
        })
        .setOrigin(0, 0);
    }

    const primaryText = this.add
      .text(width / 2, y, `LEN ${row.length} · ${row.score}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(1, 0);

    // Time is informational only - never a ranking input.
    const timeText = this.add
      .text(width / 2, y + 18, formatSurvivalTime(row.survivalTimeMs), {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#8b93a7",
      })
      .setOrigin(1, 0);

    // The row's real bottom edge - the tallest of every element actually
    // placed in it (name/badge on the left, rank/score/time on the right) -
    // never smaller than ROW_HEIGHT, so a normal single-line row keeps its
    // original, already-comfortable spacing. See `rowContentBottom()`'s own
    // doc for the wrapped-name overlap bug this replaces.
    const contentBottom = rowContentBottom(y, ROW_HEIGHT, [rankText.y + rankText.height, nameText.y + nameText.height, badge ? badge.y + badge.height : 0, primaryText.y + primaryText.height, timeText.y + timeText.height]);

    if (isMe) {
      // Sized/positioned from the same real content bottom, and added
      // FIRST (index 0) so it renders behind the text this row already
      // created above, even though it's created after them.
      const bgHeight = contentBottom - y - 2;
      const bg = this.add.rectangle(0, y + bgHeight / 2 - 2, width, bgHeight, GameConfig.colors.accent, 0.12);
      this.listContainer.addAt(bg, 0);
    }

    this.listContainer.add(rankText);
    this.listContainer.add(nameText);
    if (badge) this.listContainer.add(badge);
    this.listContainer.add(primaryText);
    this.listContainer.add(timeText);

    return contentBottom;
  }

  /**
   * Below the list: the caller's own rank (already inside the visible Top
   * 100 rows, so nothing more is shown), a separate "YOU" row when the
   * caller has a result outside the visible Top 100, or a compact JOIN
   * prompt when they haven't opted into Online Features at all - "public
   * viewing without Online Features" never blocks seeing the board, only
   * seeing/having a personal rank. Returns the Y position after whatever
   * it rendered (0/unchanged if it rendered nothing).
   */
  private renderParticipationRow(width: number, y: number, alreadyVisible = false): number {
    if (!endlessLeaderboardService.isSubmissionAvailable()) {
      const prompt = this.add
        .text(0, y, "ENABLE ONLINE FEATURES TO JOIN THE LEADERBOARD", {
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          color: "#8b93a7",
          align: "center",
          wordWrap: { width: width - 20 },
        })
        .setOrigin(0.5, 0);
      this.listContainer.add(prompt);
      const joinButtonHeight = 44;
      const joinButton = createButton(
        this,
        0,
        buttonCenterYBelow(prompt.y + prompt.height, 16, joinButtonHeight),
        "ENABLE ONLINE FEATURES",
        () => this.onlineModal.setVisible(true),
        { width: 240, height: joinButtonHeight },
      );
      this.listContainer.add(joinButton);
      return joinButton.y + 30;
    }

    if (!this.myRank || !this.myRank.hasResult || alreadyVisible) {
      return this.renderNameCta(y);
    }

    const label = this.add
      .text(-width / 2, y, "YOU", { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
      .setOrigin(0, 0);
    this.listContainer.add(label);
    y += 16;

    y = this.renderRow(
      width,
      y,
      {
        rank: this.myRank.rank ?? 0,
        displayName: "YOU",
        length: this.myRank.length,
        score: this.myRank.score,
        survivalTimeMs: this.myRank.survivalTimeMs,
        endReason: this.myRank.endReason ?? "quit",
      },
      true,
    );

    return this.renderNameCta(y);
  }

  /** A valid saved display name exists - distinct from merely being signed in/Online Features being enabled (which `isSubmissionAvailable()` above already gates on). */
  private hasValidDisplayName(): boolean {
    return !!this.myDisplayName && this.myDisplayName.trim().length > 0;
  }

  /**
   * Post-death UX: routes to the two mutually-exclusive name-entry CTAs -
   * EDIT NAME for a player who already has a valid saved name (unchanged
   * from before this PR), or the new SET NAME prompt for a player who has
   * none yet (an unnamed run's own submission was never possible before
   * this either - see `EndlessResultScene`'s doc; this is simply where
   * that choice now lives).
   */
  private renderNameCta(y: number): number {
    return this.hasValidDisplayName() ? this.renderEditNameButton(y) : this.renderSetNameCta(y);
  }

  /**
   * Following Campaign `LeaderboardScene`'s EDIT NAME pattern: always
   * offered once Online Features are enabled AND a valid name already
   * exists, whether or not the caller's own row is currently visible -
   * this is the only place a player who only ever plays Endless can
   * set/change their display name at all.
   */
  private renderEditNameButton(y: number): number {
    const editButton = createButton(this, 0, y, "EDIT NAME", () => this.openEditName(), {
      width: 160,
      height: 40,
      fontSize: "13px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.listContainer.add(editButton);
    return editButton.y + 30;
  }

  /**
   * Post-death UX: the clear CTA for a player with no valid saved name yet
   * (Online Features enabled/signed in, but never named) - a normal,
   * always-visible part of the leaderboard UI, never an automatic modal.
   * Only pressing SET NAME opens `NameEntryModal`/focuses its input.
   */
  private renderSetNameCta(y: number): number {
    const prompt = this.add
      .text(0, y, "Set your name to join the global ranking", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: Math.min(LIST_WIDTH_MAX, this.scale.width - 32) - 20 },
      })
      .setOrigin(0.5, 0);
    this.listContainer.add(prompt);
    const setNameButtonHeight = 40;
    const setNameButton = createButton(this, 0, buttonCenterYBelow(prompt.y + prompt.height, 14, setNameButtonHeight), "SET NAME", () => this.openSetName(), {
      width: 160,
      height: setNameButtonHeight,
      fontSize: "13px",
    });
    this.listContainer.add(setNameButton);
    return setNameButton.y + 30;
  }
}
